const MOBILE_QUERY = '(max-width: 989px)';
const TITLE_SELECTOR = 'body.template-product > #MainContent product-info .product__title h1';
const STICKYZOO_MULTIPLIER = 1.79;
const MINIMUM_TITLE_SIZE = 24;
const activeTitles = new Map();
const media = window.matchMedia(MOBILE_QUERY);

function visibleTitle(title) {
  return title.textContent.replace(/\s+/g, ' ').trim();
}

function measuredWidthAtBase(title, text, availableWidth, baseSize) {
  const style = window.getComputedStyle(title);
  const currentSize = Number.parseFloat(style.fontSize) || baseSize || 100;
  const currentSpacing = style.letterSpacing === 'normal' ? 0 : Number.parseFloat(style.letterSpacing) || 0;
  const spacingEm = currentSpacing / currentSize;
  const probe = document.createElement('span');
  probe.textContent = text;
  Object.assign(probe.style, {
    position: 'absolute',
    visibility: 'hidden',
    pointerEvents: 'none',
    display: 'block',
    width: `${availableWidth}px`,
    maxWidth: 'none',
    overflow: 'visible',
    whiteSpace: 'nowrap',
    fontFamily: style.fontFamily,
    fontSize: `${baseSize}px`,
    fontWeight: style.fontWeight,
    fontStyle: style.fontStyle,
    fontStretch: style.fontStretch,
    letterSpacing: `${spacingEm}em`,
    textTransform: style.textTransform,
  });
  document.body.append(probe);
  const renderedWidth = probe.scrollWidth;
  probe.remove();
  return renderedWidth;
}

function titleLineCount(title) {
  const range = document.createRange();
  range.selectNodeContents(title);
  const lineTops = new Set(
    [...range.getClientRects()]
      .filter((rect) => rect.width > 0 && rect.height > 0)
      .map((rect) => rect.top.toFixed(2)),
  );
  return lineTops.size;
}

function applyTitleSize(title, size) {
  title.style.setProperty('font-size', `${size.toFixed(6)}px`);
}

function fitTitle(title, availableWidth) {
  if (!media.matches) return;

  const text = visibleTitle(title);
  if (!text || availableWidth <= 0) return;

  const rawLength = text.length + 1;
  const baseSize = (availableWidth * STICKYZOO_MULTIPLIER) / rawLength;
  const renderedWidth = measuredWidthAtBase(title, text, availableWidth, baseSize);
  const fittedSize = Math.max(
    MINIMUM_TITLE_SIZE,
    renderedWidth > availableWidth
      ? baseSize * (availableWidth / renderedWidth)
      : baseSize,
  );

  applyTitleSize(title, fittedSize);
  if (fittedSize <= MINIMUM_TITLE_SIZE || titleLineCount(title) <= 1) return;

  const sizeForAttempt = (attempt) => Math.max(MINIMUM_TITLE_SIZE, fittedSize - attempt * 0.01);
  let lowerAttempt = 1;
  let upperAttempt = Math.min(100, Math.ceil((fittedSize - MINIMUM_TITLE_SIZE) / 0.01));

  applyTitleSize(title, sizeForAttempt(upperAttempt));
  if (titleLineCount(title) > 1) return;

  while (lowerAttempt < upperAttempt) {
    const attempt = Math.floor((lowerAttempt + upperAttempt) / 2);
    applyTitleSize(title, sizeForAttempt(attempt));
    if (titleLineCount(title) > 1) lowerAttempt = attempt + 1;
    else upperAttempt = attempt;
  }

  applyTitleSize(title, sizeForAttempt(lowerAttempt));
}

function restoreTitle(title) {
  const state = activeTitles.get(title);
  if (!state) return;

  state.observer?.disconnect();
  if (state.resizeHandler) window.removeEventListener('resize', state.resizeHandler);
  if (state.fontHandler && document.fonts) document.fonts.removeEventListener('loadingdone', state.fontHandler);

  if (state.originalSize) {
    title.style.setProperty('font-size', state.originalSize, state.originalPriority);
  } else {
    title.style.removeProperty('font-size');
  }
  activeTitles.delete(title);
}

async function observeTitle(title) {
  if (!media.matches || activeTitles.has(title)) return;

  const state = {
    originalSize: title.style.getPropertyValue('font-size'),
    originalPriority: title.style.getPropertyPriority('font-size'),
  };
  activeTitles.set(title, state);

  if (document.fonts) {
    const requestedFont = document.fonts
      .load('800 100px "LS Inter"', visibleTitle(title))
      .catch(() => []);
    await Promise.race([
      requestedFont,
      new Promise((resolve) => window.setTimeout(resolve, 1500)),
    ]);
  }

  if (!activeTitles.has(title) || !title.isConnected || !media.matches) {
    restoreTitle(title);
    return;
  }

  let lastWidth = -1;
  const update = (width = title.clientWidth, force = false) => {
    if (!media.matches) return;
    if (!force && Math.abs(width - lastWidth) < 0.01) return;
    lastWidth = width;
    fitTitle(title, width);
  };

  update();

  if ('ResizeObserver' in window) {
    state.observer = new ResizeObserver(() => update(title.clientWidth));
    state.observer.observe(title);
  } else {
    state.resizeHandler = () => update();
    window.addEventListener('resize', state.resizeHandler, { passive: true });
  }

  if (document.fonts) {
    state.fontHandler = () => update(undefined, true);
    document.fonts.addEventListener('loadingdone', state.fontHandler, { once: true });
  }
}

function initializeProductTitle() {
  if (!media.matches) return;
  const title = document.querySelector(TITLE_SELECTOR);
  if (title) observeTitle(title);
}

media.addEventListener('change', ({ matches }) => {
  if (matches) {
    initializeProductTitle();
  } else {
    for (const title of activeTitles.keys()) restoreTitle(title);
  }
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeProductTitle, { once: true });
} else {
  initializeProductTitle();
}

document.addEventListener('shopify:section:load', initializeProductTitle);
